/**
 * ChatInputArea Component
 *
 * Input area for sending messages with Enter key support
 * (Shift+Enter for newline, Enter to send).
 * Supports context tag autocomplete and validation.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Upload, message } from 'antd';
import { parseContextTag, validateContextTag } from '../../../../utils/contextTagParser';
import getFileTypeIcon from '../../utils/getFileTypeIcon';

const ACCEPTED_FILE_TYPES = '.pdf,.docx,.html,.txt,.xlsx,.xls,.csv';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ChatInputArea = ({
  value = '',
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Ask about your data...',
  recentSubjects = [], // Pass recent subjects for autocomplete
  initialContext = null, // { type: 'reports' | 'subject' } - auto-set based on route
  attachedFiles = [],
  onFilesChange = null
}) => {
  const [tagError, setTagError] = useState(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeContextTag, setActiveContextTag] = useState(null); // { type: 'reports' | 'subject', label: 'Analytics', identifier: null }
  const inputRef = useRef(null);
  const hasInitialized = useRef(false);

  // Set initial context tag based on route when component mounts
  useEffect(() => {
    if (!hasInitialized.current && initialContext && !activeContextTag) {
      hasInitialized.current = true;

      let contextLabel;
      let contextType;
      let contextIdentifier = null;
      let originalTag;

      if (initialContext.type === 'reports') {
        contextLabel = 'Analytics';
        contextType = 'reports';
        originalTag = '@reports: ';
      } else if (initialContext.type === 'subject') {
        contextType = 'subject';

        // If specific subject identifier provided, use subject-specific context
        if (initialContext.identifier) {
          contextIdentifier = initialContext.identifier;
          contextLabel = `Subject: ${initialContext.identifier}`;
          originalTag = `@subject:${initialContext.identifier}: `;
        } else {
          // General subject management context
          contextLabel = 'Subject Management';
          originalTag = '@subject: ';
        }
      }

      if (contextType) {
        setActiveContextTag({
          type: contextType,
          label: contextLabel,
          identifier: contextIdentifier,
          originalTag: originalTag
        });
      }
    }
  }, [initialContext, activeContextTag]);

  // Compute autocomplete options using useMemo (doesn't trigger re-renders)
  const autocompleteOptions = useMemo(() => {
    if (!value.startsWith('@')) {
      return [];
    }

    const afterAt = value.substring(1).toLowerCase();
    const options = [];

    // Stage 1: Show base context types
    if (afterAt === '' || 'reports'.startsWith(afterAt) || 'analytics'.startsWith(afterAt)) {
      options.push({
        type: 'context',
        value: '@reports: ',
        label: 'Analytics / Reports',
        description: 'Query aggregated analytics data and reports',
        icon: '📊',
        badge: 'Analytics',
        badgeColor: '#E6F7FF'
      });
    }

    if (afterAt === '' || 'subject'.startsWith(afterAt)) {
      options.push({
        type: 'context',
        value: '@subject: ',
        label: 'Subject Management',
        description: 'Query subject population and general management',
        icon: '👥',
        badge: 'Subject',
        badgeColor: '#F6FFED'
      });

      // Show individual subjects directly in the initial dropdown
      if (recentSubjects.length > 0) {
        const subjectSearchTerm = afterAt.length > 0 ? afterAt : '';
        const initialSubjects = recentSubjects
          .filter(s =>
            !subjectSearchTerm ||
            'subject'.startsWith(subjectSearchTerm) ||
            s.email?.toLowerCase().includes(subjectSearchTerm) ||
            s.displayId?.toLowerCase().includes(subjectSearchTerm)
          )
          .slice(0, 5);

        initialSubjects.forEach(subject => {
          options.push({
            type: 'subject',
            value: `@subject:${subject.email}: `,
            label: subject.email,
            description: `ID: ${subject.displayId || 'N/A'}`,
            icon: '👤',
            badge: 'Subject',
            badgeColor: '#F6FFED',
            subject
          });
        });
      }
    }

    // Stage 2: User typed @subject: - show recent subjects
    if (afterAt.startsWith('subject:')) {
      const afterSubject = afterAt.substring(8); // Everything after "subject:"

      // Check if user has completed the tag (typed second colon)
      // Format: @subject:identifier: message
      const secondColonIndex = afterSubject.indexOf(':');

      // If there's a second colon followed by space, tag is complete - don't show autocomplete
      if (secondColonIndex !== -1 && afterSubject[secondColonIndex + 1] === ' ') {
        return []; // Tag is complete, hide autocomplete
      }

      // Extract search term (everything before second colon, or everything if no second colon)
      const searchTerm = secondColonIndex !== -1
        ? afterSubject.substring(0, secondColonIndex).toLowerCase()
        : afterSubject.toLowerCase();

      // Add "All records" option first
      if (searchTerm === '') {
        options.push({
          type: 'context',
          value: '@subject: ',
          label: 'All records',
          description: 'Query all subjects and subject management data',
          icon: '👥',
          badge: 'Subject',
          badgeColor: '#F6FFED'
        });
      }

      // Filter and show matching subjects
      const matchingSubjects = recentSubjects
        .filter(s =>
          !searchTerm ||
          s.email?.toLowerCase().includes(searchTerm) ||
          s.displayId?.toLowerCase().includes(searchTerm)
        )
        .slice(0, 8); // Show top 8 matches

      matchingSubjects.forEach(subject => {
        options.push({
          type: 'subject',
          value: `@subject:${subject.email}: `,
          label: subject.email,
          description: `ID: ${subject.displayId || 'N/A'}`,
          icon: '👤',
          badge: 'Subject',
          badgeColor: '#F6FFED',
          subject
        });
      });


      // Show "no matches" if search term exists but no results
      if (searchTerm && matchingSubjects.length === 0) {
        options.push({
          type: 'error',
          value: value, // Keep current value
          label: 'No subjects found',
          description: `No match for "${searchTerm}"`,
          icon: '❌',
          isError: true
        });
      }
    }


    return options;
  }, [value, recentSubjects]);

  // Separate effect to update show/hide state
  useEffect(() => {
    const shouldShow = value.startsWith('@') && autocompleteOptions.length > 0;

    if (shouldShow !== showAutocomplete) {
      setShowAutocomplete(shouldShow);
      if (shouldShow) {
        setSelectedIndex(0);
      }
    }
  }, [value, autocompleteOptions.length, showAutocomplete]);

  // Resolve a tag string like "@reports: " or "@subject:email: " into a badge config
  const resolveTagToBadge = (tagValue) => {
    // Match @reports:, @analytics:, @subject:, @subject:identifier:
    const tagMatch = tagValue.match(/^@(reports|analytics|subject)(?::([^\s:]+))?\s*:\s*$/i);
    if (!tagMatch) return null;

    const rawType = tagMatch[1].toLowerCase();
    const identifier = tagMatch[2] || null;

    if (rawType === 'reports' || rawType === 'analytics') {
      return {
        type: 'reports',
        label: 'Analytics',
        identifier: null,
        originalTag: '@reports: '
      };
    }

    if (rawType === 'subject') {
      return {
        type: 'subject',
        label: identifier ? `Subject: ${identifier}` : 'Subject Management',
        identifier,
        originalTag: tagValue
      };
    }

    return null;
  };

  // Validate tag on input change
  const handleChange = (e) => {
    const newValue = e.target.value;

    // Check if user just completed a context tag (ends with ": " - tag complete with space)
    if (newValue.startsWith('@') && newValue.endsWith(': ')) {
      const badgeInfo = resolveTagToBadge(newValue);
      if (badgeInfo) {
        setActiveContextTag(badgeInfo);
        onChange({ target: { value: '' } });
        setTagError(null);
        setShowAutocomplete(false);
        return;
      }
    }

    // Validate ongoing tag
    if (newValue.startsWith('@')) {
      const parsed = parseContextTag(newValue + ' '); // Add space to test if it would be valid when complete
      const validation = validateContextTag(parsed);
      setTagError(validation.valid ? null : validation.error);
    } else {
      setTagError(null);
    }

    onChange(e);
  };

  // Remove active context tag
  const handleRemoveContextTag = () => {
    setActiveContextTag(null);
  };

  // File upload handlers
  const handleBeforeUpload = (file) => {
    const documentTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/html',
      'text/plain',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    const documentExtensions = ['.pdf', '.docx', '.html', '.txt', '.xlsx', '.xls', '.csv'];

    const isDocument = documentTypes.includes(file.type) ||
                       documentExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

    if (!isDocument) {
      console.log('[ChatInputArea] File rejected - unsupported type:', { name: file.name, type: file.type, size: file.size });
      message.error(`${file.name}: Only PDF, DOCX, HTML, TXT, Excel, and CSV files are supported`);
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      console.log('[ChatInputArea] File rejected - too large:', { name: file.name, size: file.size, maxSize: MAX_FILE_SIZE });
      message.error(`${file.name}: File must be smaller than ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      return false;
    }

    console.log('[ChatInputArea] File accepted:', { name: file.name, type: file.type, size: file.size });

    if (onFilesChange) {
      onFilesChange([...attachedFiles, file]);
    }

    return false; // Prevent auto upload
  };

  const handleRemoveFile = (fileToRemove) => {
    console.log('[ChatInputArea] File removed:', { name: fileToRemove.name, type: fileToRemove.type });
    if (onFilesChange) {
      onFilesChange(attachedFiles.filter(f => f !== fileToRemove));
    }
  };

  const handleKeyDown = (e) => {
    // Handle autocomplete navigation
    if (showAutocomplete) {
      // Filter out non-selectable items
      const selectableOptions = autocompleteOptions.filter(
        opt => !opt.isDivider && !opt.isHint && !opt.isError
      );

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const currentSelectableIndex = selectableOptions.findIndex(
          opt => opt === autocompleteOptions[selectedIndex]
        );
        const nextIndex = (currentSelectableIndex + 1) % selectableOptions.length;
        const nextOption = selectableOptions[nextIndex];
        const actualIndex = autocompleteOptions.indexOf(nextOption);
        setSelectedIndex(actualIndex);
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const currentSelectableIndex = selectableOptions.findIndex(
          opt => opt === autocompleteOptions[selectedIndex]
        );
        const prevIndex = currentSelectableIndex <= 0
          ? selectableOptions.length - 1
          : currentSelectableIndex - 1;
        const prevOption = selectableOptions[prevIndex];
        const actualIndex = autocompleteOptions.indexOf(prevOption);
        setSelectedIndex(actualIndex);
        return;
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        const selected = autocompleteOptions[selectedIndex];
        if (selected && !selected.isDivider && !selected.isHint && !selected.isError) {
          e.preventDefault();
          handleAutocompleteClick(selected);
          return;
        }
      } else if (e.key === 'Escape') {
        setShowAutocomplete(false);
        return;
      }
    }

    // Regular send on Enter (no Shift)
    if (e.key === 'Enter' && !e.shiftKey && !showAutocomplete) {
      e.preventDefault();
      if (onSend && (value.trim() || attachedFiles.length > 0) && !disabled && !tagError) {
        // Use active tag if set, otherwise fall back to initial context (route-based default)
        const contextToUse = activeContextTag || (initialContext ? {
          type: initialContext.type,
          label: initialContext.type === 'reports'
            ? 'Analytics'
            : (initialContext.identifier
              ? `Subject: ${initialContext.identifier}`
              : 'Subject Management'),
          identifier: initialContext.identifier || null,
          originalTag: initialContext.type === 'reports'
            ? '@reports: '
            : (initialContext.identifier
              ? `@subject:${initialContext.identifier}: `
              : '@subject: ')
        } : null);

        if (contextToUse) {
          const messageWithTag = contextToUse.originalTag + value;
          onSend(messageWithTag, attachedFiles);
        } else {
          onSend(undefined, attachedFiles);
        }
      }
    }
  };

  const handleAutocompleteClick = (option) => {
    if (option.isDivider || option.isHint || option.isError) return;

    // When selecting an autocomplete option, apply it as a badge
    const selectedValue = option.value;

    // Check if it's a complete tag (ends with ": ")
    if (selectedValue.endsWith(': ')) {
      const badgeInfo = resolveTagToBadge(selectedValue);
      if (badgeInfo) {
        setActiveContextTag(badgeInfo);
        onChange({ target: { value: '' } });
        setShowAutocomplete(false);
        inputRef.current?.focus();
        return;
      }
    }

    // Fallback: just set the value normally
    onChange({ target: { value: selectedValue } });
    setShowAutocomplete(false);
    inputRef.current?.focus();
  };

  const handleSendClick = () => {
    if (onSend && (value.trim() || attachedFiles.length > 0) && !disabled && !tagError) {
      // Use active tag if set, otherwise fall back to initial context (route-based default)
      const contextToUse = activeContextTag || (initialContext ? {
        type: initialContext.type,
        label: initialContext.type === 'reports'
          ? 'Analytics'
          : (initialContext.identifier
            ? `Subject: ${initialContext.identifier}`
            : 'Subject Management'),
        identifier: initialContext.identifier || null,
        originalTag: initialContext.type === 'reports'
          ? '@reports: '
          : (initialContext.identifier
            ? `@subject:${initialContext.identifier}: `
            : '@subject: ')
      } : null);

      if (contextToUse) {
        const messageWithTag = contextToUse.originalTag + value;
        onSend(messageWithTag, attachedFiles);
      } else {
        onSend(undefined, attachedFiles);
      }
    }
  };

  return (
    <div className="reports-chatbot-input" style={{ position: 'relative' }}>
      {tagError && (
        <div className="tag-error" style={{
          color: '#ff4d4f',
          fontSize: '12px',
          padding: '6px 12px',
          marginBottom: '6px',
          background: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span>⚠️</span>
          <span>{tagError}</span>
        </div>
      )}

      {showAutocomplete && (
        <div className="tag-autocomplete" style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          right: 0,
          background: 'white',
          border: '1px solid #d9d9d9',
          borderRadius: '8px',
          marginBottom: '8px',
          maxHeight: '400px',
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}>
          {autocompleteOptions.map((option, idx) => {
            // Render divider
            if (option.isDivider) {
              return (
                <div
                  key={idx}
                  style={{
                    padding: '8px 12px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#8c8c8c',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderTop: idx > 0 ? '1px solid #f0f0f0' : 'none',
                    background: '#fafafa'
                  }}
                >
                  {option.label}
                </div>
              );
            }

            // Render hint
            if (option.isHint) {
              return (
                <div
                  key={idx}
                  style={{
                    padding: '12px',
                    fontSize: '12px',
                    color: '#595959',
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    margin: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>{option.icon}</span>
                  <div>
                    <div style={{ fontWeight: 500, marginBottom: '2px' }}>
                      {option.label}
                    </div>
                    <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                      {option.description}
                    </div>
                  </div>
                </div>
              );
            }

            // Render error
            if (option.isError) {
              return (
                <div
                  key={idx}
                  style={{
                    padding: '12px',
                    fontSize: '12px',
                    color: '#595959',
                    background: '#fff2f0',
                    border: '1px solid #ffccc7',
                    margin: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>{option.icon}</span>
                  <div>
                    <div style={{ fontWeight: 500, color: '#ff4d4f' }}>
                      {option.label}
                    </div>
                    <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                      {option.description}
                    </div>
                  </div>
                </div>
              );
            }

            // Render regular option
            const isSelected = idx === selectedIndex;
            const isExample = option.isExample;

            return (
              <div
                key={idx}
                className={`autocomplete-option ${isSelected ? 'selected' : ''}`}
                onClick={() => handleAutocompleteClick(option)}
                onMouseEnter={() => setSelectedIndex(idx)}
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  background: isSelected ? '#f5f5f5' : 'white',
                  borderBottom: idx < autocompleteOptions.length - 1 ? '1px solid #f0f0f0' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'background 0.2s',
                  opacity: isExample ? 0.7 : 1
                }}
              >
                <span style={{ fontSize: '20px', flexShrink: 0 }}>
                  {option.icon}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 500,
                    fontSize: '13px',
                    marginBottom: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {option.label}
                    </span>
                    {option.badge && (
                      <span style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: option.badgeColor,
                        color: option.badge === 'Analytics' ? '#0050B3' : '#389E0D',
                        fontWeight: 600,
                        flexShrink: 0
                      }}>
                        {option.badge}
                      </span>
                    )}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#8c8c8c',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {option.description}
                  </div>
                </div>
                {isSelected && (
                  <span style={{
                    fontSize: '11px',
                    color: '#8c8c8c',
                    flexShrink: 0
                  }}>
                    ↵
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="reports-chatbot-input-wrapper">
        {/* Context tag badge and file attachments - displayed inside input area, above input field */}
        {(activeContextTag || attachedFiles.length > 0) && (
          <div className="chatbot-attachments-list">
            {activeContextTag && (
              <div className="chatbot-attachment-item context-tag-badge">
                <div className="attachment-icon">
                  {activeContextTag.type === 'reports' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,3H5C3.9,3 3,3.9 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.9 20.1,3 19,3M9,17H7V10H9V17M13,17H11V7H13V17M17,17H15V13H17V17Z"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                    </svg>
                  )}
                </div>
                <div className="attachment-info">
                  <span className="attachment-name">{activeContextTag.label}</span>
                </div>
                <span className="attachment-separator"></span>
                <button
                  onClick={handleRemoveContextTag}
                  className="chatbot-remove-attachment"
                  aria-label="Remove context tag"
                  title="Remove context tag"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M11.53 4.47a.75.75 0 0 0-1.06 0L8 6.94 5.53 4.47a.75.75 0 0 0-1.06 1.06L6.94 8l-2.47 2.47a.75.75 0 1 0 1.06 1.06L8 9.06l2.47 2.47a.75.75 0 1 0 1.06-1.06L9.06 8l2.47-2.47a.75.75 0 0 0 0-1.06Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            )}
            {attachedFiles.map((file, idx) => {
              return (
                <div key={idx} className="chatbot-attachment-item">
                  <div className="attachment-icon">
                    {getFileTypeIcon(file.name, file.type)}
                  </div>
                  <div className="attachment-info">
                    <span className="attachment-name">{file.name}</span>
                  </div>
                  <span className="attachment-separator"></span>
                  <button
                    onClick={() => handleRemoveFile(file)}
                    className="chatbot-remove-attachment"
                    aria-label="Remove file"
                    title="Remove file"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path fillRule="evenodd" clipRule="evenodd" d="M11.53 4.47a.75.75 0 0 0-1.06 0L8 6.94 5.53 4.47a.75.75 0 0 0-1.06 1.06L6.94 8l-2.47 2.47a.75.75 0 1 0 1.06 1.06L8 9.06l2.47 2.47a.75.75 0 1 0 1.06-1.06L9.06 8l2.47-2.47a.75.75 0 0 0 0-1.06Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Ask about your data... (try @reports: or @subject:)"}
          disabled={disabled}
          data-testid="ai-input"
        />

        {/* File upload button */}
        {onFilesChange && (
          <Upload
            accept={ACCEPTED_FILE_TYPES}
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            multiple
            disabled={disabled}
          >
            <button
              className="chatbot-attach-button"
              disabled={disabled}
              title="Attach files (PDF, DOCX, HTML, TXT, Excel, CSV)"
              aria-label="Attach files"
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.5,6V17.5A4,4 0 0,1 12.5,21.5A4,4 0 0,1 8.5,17.5V5A2.5,2.5 0 0,1 11,2.5A2.5,2.5 0 0,1 13.5,5V15.5A1,1 0 0,1 12.5,16.5A1,1 0 0,1 11.5,15.5V6H10V15.5A2.5,2.5 0 0,0 12.5,18A2.5,2.5 0 0,0 15,15.5V5A4,4 0 0,0 11,1A4,4 0 0,0 7,5V17.5A5.5,5.5 0 0,0 12.5,23A5.5,5.5 0 0,0 18,17.5V6H16.5Z"/>
              </svg>
            </button>
          </Upload>
        )}

        {(() => {
          const isSendDisabled = (!value.trim() && attachedFiles.length === 0) || disabled || !!tagError;
          return (
            <button
              className="reports-chatbot-send-button"
              onClick={handleSendClick}
              disabled={isSendDisabled}
              title="Send message"
              aria-label="Send message"
              data-testid="ai-send"
            >
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="8" fill={isSendDisabled ? '#b2b2c7' : '#0141E4'}/>
                <path d="M16 23V9" stroke="#F7F7F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M11 14L16 9L21 14" stroke="#F7F7F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          );
        })()}
      </div>
    </div>
  );
};

export default ChatInputArea;
